"use client";

import { useCallback, useState } from "react";

import { useAuth } from "@/hooks/useAuth";
import { useTenant } from "@/hooks/useTenant";
import { useCftcAcknowledgement } from "@/components/trade/FirstTradeDisclosureModal";
import { isTenantGateReady } from "@/components/tenant/TenantContextProvider";

type GatedAction = () => void | Promise<void>;

export interface UseCftcGateReturn {
  runGated: (action: GatedAction) => void;
  reset: () => void;
  modalProps: {
    open: boolean;
    onAcknowledge: () => void;
    onClose: () => void;
  };
}

/**
 * SFWL-017: shared CFTC §1.55(b) risk-disclosure acknowledgement gate.
 *
 * Composes `useTenant()` + `useAuth()` + `useCftcAcknowledgement(tenantId,
 * userId)` (the same primitive `FirstTradeDisclosureModal.tsx` exposes for
 * SFWL-007's `PaperTradingPanel`), so the persisted ack is shared across
 * flows under the `${tenantId}:${userId}:cftc-ack` localStorage key —
 * whichever flow acknowledges first satisfies the gate everywhere.
 *
 * `runGated(action)`:
 *   - Fail-closed (Codex P1, PR #116): while `!isTenantGateReady(tenant.resolution)`
 *     (i.e. `resolution` is `"pending"` or `"failed"` — see
 *     `TenantContextProvider.tsx`), `action` is neither run NOR stashed, and
 *     the modal does not open. `tenant.marketScope` is untrustworthy until
 *     resolution finishes (it may still read the `DEFAULT_TENANT_CONTEXT`
 *     value `"all"` for what is actually a futures tenant), so this hook
 *     refuses to make any gating decision on it. Consumers are expected to
 *     already disable their own submit path during this window; this check
 *     is defense-in-depth so a race, or a future consumer that forgets the
 *     disable, cannot slip an ungated action through.
 *   - Otherwise, futures tenant (`tenant.marketScope === "futures"`) with no
 *     persisted ack: stashes `action` and opens the disclosure modal.
 *     `action` is NOT invoked yet.
 *   - Otherwise (non-futures tenant, or a persisted ack already exists):
 *     invokes `action` immediately and the modal never opens.
 *
 * Stash semantics are REPLACE, not queue: calling `runGated` again while the
 * modal is already open discards the previously stashed action in favor of
 * the new one. Acknowledging always fires exactly one action — the most
 * recently stashed — never more than once and never a stale earlier one.
 *
 * `modalProps` wires directly into `<FirstTradeDisclosureModal {...modalProps} />`:
 *   - `onAcknowledge`: persists the ack via `useCftcAcknowledgement`, runs
 *     the currently-stashed action exactly once, then closes the modal.
 *   - `onClose`: discards the stashed action WITHOUT running it and closes
 *     the modal. The gate remains unacknowledged, so the next `runGated`
 *     call re-opens the modal.
 *
 * `reset()`: clears any stashed action (and therefore closes the modal, since
 * `modalProps.open` is derived from `pendingAction !== null`), without
 * touching the persisted ack. This exists because `pendingAction` is state
 * owned by THIS hook instance, not by the consumer's own dialog — if a
 * consumer's modal/drawer closes or unmounts-and-remounts-later while the
 * disclosure is still stashed (e.g. the consumer early-returns `null` while
 * staying mounted, so `useCftcGate` itself never unmounts), the stashed
 * action survives and a later, unrelated `runGated` replaces it correctly,
 * but if the consumer instead re-opens without calling `runGated` again the
 * stale stash can leak through. Consumers MUST call `reset()` whenever they
 * close/unmount their own dialog while the disclosure may be stashed, so a
 * later reopen never auto-opens the disclosure for — or fires — a stale
 * action from a previous attempt. A no-op when nothing is pending.
 *
 * This is a component-layer enforcement decision (see the locked spec,
 * docs/features/epics/sweet-futures/SFWL-017-cftc-ack-gate.md) — each
 * consumer is responsible for routing its own opening-order/allocation
 * submit handler through `runGated`. A hook-layer promise gate (singleton
 * provider + `requireAck()`) was considered and rejected: its
 * missing-provider fallback would silently degrade to ungated, which
 * defeats the guarantee exactly where it matters.
 *
 * Consumers: `AllocationModal` (strategy allocation submit),
 * `ApplyConfirmationModal` (portfolio-builder apply submit), and
 * `PaperTradingPanel` (order submit, reduce-only orders bypass the gate
 * entirely) — its SFWL-007 inline wiring has migrated onto this shared hook
 * (commit ce88670), with an ack-time price ref added in b37c16b.
 *
 * P3 fix-up: consumers currently early-return `null` on their own "closed"
 * state while staying mounted, which keeps this hook instance alive with a
 * stashed action across their own dialog's close. Wiring each consumer's
 * close/unmount path to call `reset()` is tracked as follow-up work owned by
 * frontend-ui, not part of this hook.
 */
export function useCftcGate(): UseCftcGateReturn {
  const tenant = useTenant();
  const { session } = useAuth();
  const tenantId = tenant.id ?? "default";
  const userId = session?.user?.id ?? "anonymous";
  const { acknowledged, acknowledge } = useCftcAcknowledgement(tenantId, userId);

  const [pendingAction, setPendingAction] = useState<GatedAction | null>(null);

  const runGated = useCallback(
    (action: GatedAction) => {
      if (!isTenantGateReady(tenant.resolution)) {
        // Fail closed: neither run nor stash. Callers should already disable
        // their submit path while resolution is pending/failed — this is
        // defense-in-depth so a race or a future consumer that forgets the
        // disable can't slip an ungated action through while marketScope is
        // untrustworthy.
        return;
      }
      if (tenant.marketScope === "futures" && !acknowledged) {
        setPendingAction(() => action);
        return;
      }
      void action();
    },
    [tenant.resolution, tenant.marketScope, acknowledged]
  );

  const onAcknowledge = useCallback(() => {
    acknowledge();
    const action = pendingAction;
    setPendingAction(null);
    if (action) void action();
  }, [acknowledge, pendingAction]);

  const onClose = useCallback(() => {
    setPendingAction(null);
  }, []);

  const reset = useCallback(() => {
    setPendingAction(null);
  }, []);

  return {
    runGated,
    reset,
    modalProps: { open: pendingAction !== null, onAcknowledge, onClose },
  };
}
